import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ArrowLeft, PauseCircle, PlayCircle, FastForward, XCircle, MapPinPlus, FileText, Printer, Mail, Phone } from "lucide-react";
import { useApi, useAdminSession, fmtDate, fmtDay, fmtUSD } from "@/lib/api";
import { StatusBadge, PaymentBadge } from "@/components/StatusBadge";
import { ActionDialog } from "@/components/admin/ActionDialog";
import { EventDialog } from "@/components/admin/EventDialog";
import { BillingOverview, BillingEditor } from "@/components/admin/BillingPanel";
import { PaymentsPanel } from "@/components/admin/PaymentsPanel";

const STAGES = ["Booked", "Picked Up", "In Transit", "Arrived at Hub", "Customs Clearance", "Out for Delivery", "Delivered"];
const nextStage = (st) => (st === "Pending Approval" ? "Booked" : STAGES.includes(st) && st !== "Delivered" ? STAGES[STAGES.indexOf(st) + 1] : null);

const ActBtn = ({ icon: Icon, label, onClick, disabled, cls, testId }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testId}
    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-sm text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}><Icon className="h-4 w-4" />{label}</button>
);

const Party = ({ title, p }) => (
  <div className="space-y-1 text-sm">
    <p className="text-xs uppercase tracking-wider text-slate-500">{title}</p>
    <p className="font-semibold">{p.name}</p>
    <p className="text-slate-600">{p.address}</p>
    {p.email && <a href={`mailto:${p.email}`} className="flex items-center gap-1 text-[#FF6B00]"><Mail className="h-3.5 w-3.5" />{p.email}</a>}
    {p.phone && <a href={`tel:${p.phone}`} className="flex items-center gap-1 text-slate-600"><Phone className="h-3.5 w-3.5" />{p.phone}</a>}
  </div>
);

function Activity({ items }) {
  const list = [...(items || [])].reverse();
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="activity-history">
      <h3 className="font-display font-semibold text-[#0A192F] mb-4">Activity history</h3>
      {!list.length && <p className="text-sm text-slate-500">No admin actions recorded yet.</p>}
      <ol className="space-y-3">
        {list.map((a) => (
          <li key={a.id} className="border-l-2 border-[#FF6B00] pl-3 text-sm" data-testid={`activity-${a.id}`}>
            <p className="font-semibold">{a.action}{a.from_status && a.to_status && a.from_status !== a.to_status ? `: ${a.from_status} → ${a.to_status}` : ""}</p>
            {a.reason && <p className="text-slate-600">Reason: {a.reason}</p>}
            {a.details && <p className="text-slate-500">{a.details}</p>}
            <p className="text-xs font-mono text-slate-400">{fmtDate(a.at)} · by {a.by}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Manage({ id }) {
  const api = useApi();
  const q = useQuery({ queryKey: ["admin-shipment", id], queryFn: () => api("get", `/admin/shipments/${id}`), staleTime: 0, retry: false });
  const [action, setAction] = useState(null);
  const [eventOpen, setEventOpen] = useState(false);
  if (q.isLoading) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  if (q.isError) return <p className="text-slate-500" data-testid="manage-not-found">Shipment not found.</p>;
  const s = q.data;
  const st = s.status;
  const nxt = nextStage(st);
  const sum = s.billing_summary;
  const releaseBlocked = st === "On Hold" && sum.payment_required_for_release && sum.balance > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <p className="font-mono font-bold tracking-wider text-2xl sm:text-3xl text-[#FF6B00]" data-testid="manage-tracking-number">{s.tracking_number}</p>
          <div className="flex flex-wrap gap-2"><StatusBadge status={st} testId="manage-status" /><PaymentBadge status={sum.status} testId="manage-payment-status" /></div>
          <p className="font-display font-semibold text-[#0A192F]">{s.origin} → {s.destination}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/admin/invoice/${s.id}`} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border border-slate-300 rounded-sm hover:border-[#0A192F]" data-testid="manage-invoice-link"><FileText className="h-4 w-4" /> Invoice</Link>
          <Link to={`/admin/label/${s.id}`} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border border-slate-300 rounded-sm hover:border-[#0A192F]" data-testid="manage-label-link"><Printer className="h-4 w-4" /> Label</Link>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap gap-2" data-testid="manage-actions">
        <ActBtn icon={PauseCircle} label="Hold" cls="bg-amber-500 hover:bg-amber-600" disabled={["On Hold", "Delivered", "Cancelled"].includes(st)} onClick={() => setAction("hold")} testId="action-hold-btn" />
        <ActBtn icon={PlayCircle} label="Release" cls="bg-emerald-600 hover:bg-emerald-700" disabled={st !== "On Hold" || releaseBlocked} onClick={() => setAction("release")} testId="action-release-btn" />
        <ActBtn icon={FastForward} label={nxt ? `Proceed → ${nxt}` : "Proceed"} cls="bg-[#0A192F] hover:bg-[#112240]" disabled={!nxt || st === "On Hold"} onClick={() => setAction("proceed")} testId="action-proceed-btn" />
        <ActBtn icon={MapPinPlus} label="Update status" cls="bg-[#FF6B00] hover:bg-[#E05D00]" onClick={() => setEventOpen(true)} disabled={st === "On Hold" || st === "Cancelled"} testId="action-update-status-btn" />
        <ActBtn icon={XCircle} label="Cancel" cls="bg-red-600 hover:bg-red-700" disabled={["Delivered", "Cancelled"].includes(st)} onClick={() => setAction("cancel")} testId="action-cancel-btn" />
        {releaseBlocked && <p className="w-full text-sm text-amber-700" data-testid="release-blocked-msg">Release is locked until the {fmtUSD(sum.balance)} balance is paid.</p>}
      </div>
      <BillingOverview summary={sum} />
      <div className="grid lg:grid-cols-2 gap-6">
        <BillingEditor shipment={s} onSaved={q.refetch} />
        <PaymentsPanel shipment={s} onSaved={q.refetch} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6 grid sm:grid-cols-2 gap-6" data-testid="manage-details">
          <Party title="Sender" p={s.sender} />
          <Party title="Receiver" p={s.receiver} />
          <div className="space-y-1 text-sm"><p className="text-xs uppercase tracking-wider text-slate-500">Customer account</p>
            {s.customer ? <><p className="font-semibold">{s.customer.name}</p><p className="text-slate-600 break-all">{s.customer.email}</p></> : <p className="text-slate-500">Created by admin</p>}</div>
          <div className="space-y-1 text-sm"><p className="text-xs uppercase tracking-wider text-slate-500">Package</p>
            <p>{s.package.contents} · {s.package.weight_kg} kg × {s.package.quantity}</p><p className="text-slate-600">{s.package.service} · booked {fmtDay(s.created_at)}</p>
            <p className="text-slate-600">Now: {s.current_location?.name}</p></div>
        </div>
        <Activity items={s.activity} />
      </div>
      <ActionDialog action={action} shipment={s} nextStage={nxt} onOpenChange={(o) => !o && setAction(null)} onDone={q.refetch} />
      <EventDialog open={eventOpen} shipment={s} onOpenChange={setEventOpen} onSaved={q.refetch} />
    </div>
  );
}

export default function AdminShipmentPage() {
  const { id } = useParams();
  const { data: session, isLoading, isError } = useAdminSession();
  if (isError) return <Navigate to="/admin/login" replace />;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#0A192F]" data-testid="manage-back-link"><ArrowLeft className="h-4 w-4" /> Back to admin</Link>
      {isLoading || !session ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : <Manage id={id} />}
    </div>
  );
}
